/**
 * OrgOneDriveShares
 *
 * Displays OneDrive directories shared by org members.
 * Org members can browse and download files through this component.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Cloud,
  Folder,
  FileText,
  Download,
  ChevronRight,
  ArrowLeft,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { toast } from 'sonner';
import { FileService, OrgOneDriveShare, OneDriveFileItem } from '@/services/api/fileService';
import { FOREST_GREEN } from '@/styles/tokens';

interface Props {
  orgSlug: string;
}

export const OrgOneDriveShares: React.FC<Props> = ({ orgSlug }) => {
  const [shares, setShares] = useState<OrgOneDriveShare[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Browsing state
  const [activeShareId, setActiveShareId] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState('');
  const [files, setFiles] = useState<OneDriveFileItem[]>([]);
  const [isBrowsing, setIsBrowsing] = useState(false);
  const [pathHistory, setPathHistory] = useState<string[]>([]);

  const fetchShares = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await FileService.listOrgOneDriveShares(orgSlug);
      if (response.success && response.data) {
        setShares(response.data as OrgOneDriveShare[]);
      }
    } catch {
      // Silently fail
    }
    setIsLoading(false);
  }, [orgSlug]);

  useEffect(() => {
    fetchShares();
  }, [fetchShares]);

  const handleBrowse = useCallback(async (shareId: string, path: string) => {
    setIsBrowsing(true);
    setActiveShareId(shareId);
    try {
      const response = await FileService.browseSharedOneDrive(orgSlug, shareId, path);
      if (response.success && response.data) {
        setFiles(response.data as OneDriveFileItem[]);
        setCurrentPath(path);
      }
    } catch {
      toast.error('Failed to browse shared folder');
    }
    setIsBrowsing(false);
  }, [orgSlug]);

  const handleNavigateInto = useCallback((shareId: string, folderPath: string) => {
    setPathHistory((prev) => [...prev, currentPath]);
    handleBrowse(shareId, folderPath);
  }, [currentPath, handleBrowse]);

  const handleNavigateBack = useCallback(() => {
    const prev = pathHistory[pathHistory.length - 1];
    if (prev !== undefined && activeShareId) {
      setPathHistory((h) => h.slice(0, -1));
      handleBrowse(activeShareId, prev);
    } else {
      // Go back to share list
      setActiveShareId(null);
      setFiles([]);
      setCurrentPath('');
      setPathHistory([]);
    }
  }, [pathHistory, activeShareId, handleBrowse]);

  const handleDownload = useCallback(async (shareId: string, filePath: string) => {
    try {
      const response = await FileService.getSharedOneDriveDownloadUrl(orgSlug, shareId, filePath);
      if (response.success && (response.data as any)?.downloadUrl) {
        window.open((response.data as any).downloadUrl, '_blank');
      } else {
        toast.error('Failed to get download URL');
      }
    } catch {
      toast.error('Failed to download file');
    }
  }, [orgSlug]);

  const formatSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Loading shared drives...</span>
        </CardContent>
      </Card>
    );
  }

  if (shares.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Cloud className="h-5 w-5" />
            Shared OneDrive Folders
          </CardTitle>
          <CardDescription>
            No OneDrive folders have been shared with this organization yet.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // Browsing a specific share's files
  if (activeShareId) {
    const activeShare = shares.find((s) => s.shareId === activeShareId);
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={handleNavigateBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Cloud className="h-5 w-5" style={{ color: FOREST_GREEN.DEFAULT }} />
                {activeShare?.ownerName} — {activeShare?.label}
              </CardTitle>
              <CardDescription className="text-xs mt-1">
                {currentPath}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isBrowsing ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : files.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              This folder is empty.
            </p>
          ) : (
            <div className="space-y-1">
              {/* Folders first, then files */}
              {[...files].sort((a, b) => {
                if (a.isFolder !== b.isFolder) return a.isFolder ? -1 : 1;
                return a.name.localeCompare(b.name);
              }).map((file) => (
                <div
                  key={file.path}
                  className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-muted/50 cursor-pointer group"
                  onClick={() => {
                    if (file.isFolder) handleNavigateInto(activeShareId, file.path);
                  }}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {file.isFolder ? (
                      <Folder className="h-4 w-4 text-amber-500 shrink-0" />
                    ) : (
                      <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                    )}
                    <span className="text-sm truncate">{file.name}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {!file.isFolder && (
                      <>
                        <span className="text-xs text-muted-foreground">{formatSize(file.size)}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(activeShareId, file.path);
                          }}
                        >
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                    {file.isFolder && (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Share list view
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Cloud className="h-5 w-5" />
          Shared OneDrive Folders
        </CardTitle>
        <CardDescription>
          OneDrive folders shared by organization members.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {shares.map((share) => (
            <div key={share.shareId} className="border rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded bg-[#0078D4] flex items-center justify-center text-white text-[10px] font-bold">
                    OD
                  </div>
                  <div>
                    <p className="text-sm font-medium">
                      {share.ownerName} — {share.label}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {share.permissions === 'read' ? 'Read only' : 'Read & Write'}
                    </p>
                  </div>
                </div>
                <Badge variant="outline" className="text-xs">
                  {share.sharedPaths.length} {share.sharedPaths.length === 1 ? 'folder' : 'folders'}
                </Badge>
              </div>
              <div className="mt-2 space-y-1">
                {share.sharedPaths.map((sp) => (
                  <Button
                    key={sp}
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start h-7 text-xs"
                    onClick={() => handleBrowse(share.shareId, sp)}
                  >
                    <Folder className="h-3.5 w-3.5 mr-1.5 text-amber-500" />
                    {sp}
                    <ChevronRight className="h-3 w-3 ml-auto text-muted-foreground" />
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
